import React from "react";
import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
  renderBulletPoints,
} from "../Services/ReactPDFService.jsx";

// Clean Professional Template - Two-column layout with section labels on left
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
    paddingHorizontal: 40,
    paddingTop: 40,
    paddingBottom: 40,
    fontFamily: "Roboto",
  },
  // Header section
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingBottom: 15,
    marginBottom: 20,
  },
  // Name styling - bold, larger
  name: {
    fontSize: 28,
    fontWeight: 700,
    color: "#000000",
    letterSpacing: 0.5,
  },
  // Professional title - italic, right-aligned
  title: {
    fontSize: 14,
    color: "#000000",
    fontStyle: "italic",
  },
  // Section row - label on left, content on right
  sectionRow: {
    flexDirection: "row",
    marginBottom: 20,
  },
  // Section label column
  sectionLabelColumn: {
    width: "20%",
    // paddingRight: 15,
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#000000",
    textTransform: "uppercase",
    letterSpacing: 1,
    paddingTop: 5,
  },
  // Section content column
  sectionContentColumn: {
    width: "80%",
    // paddingLeft: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    paddingBottom: 5,
    paddingTop: 5,
  },
  // Contact section - two columns
  contactGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  contactColumn: {
    width: "50%",
    marginBottom: 4,
  },
  contactItem: {
    flexDirection: "row",
    marginBottom: 3,
  },
  contactLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#000000",
    marginRight: 4,
  },
  contactValue: {
    fontSize: 10,
    color: "#000000",
  },
  // Experience items
  experienceItem: {
    marginBottom: 14,
  },
  experienceHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  jobTitleDate: {
    fontSize: 11,
    fontWeight: 700,
    color: "#000000",
  },
  companyName: {
    fontSize: 10,
    color: "#000000",
    marginBottom: 4,
  },
  bulletPoint: {
    fontSize: 10,
    marginBottom: 3,
    paddingLeft: 12,
    lineHeight: 1.5,
    color: "#000000",
  },
  // Education items
  educationItem: {
    marginBottom: 12,
  },
  schoolDateRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  schoolName: {
    fontSize: 11,
    fontWeight: 700,
    color: "#000000",
  },
  degree: {
    fontSize: 10,
    color: "#000000",
    marginTop: 2,
    marginBottom: 4,
  },
  // Certifications - two column grid
  certificationsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  certificationItem: {
    width: "50%",
    marginBottom: 10,
    paddingRight: 10,
  },
  certName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#000000",
  },
  certAuthority: {
    fontSize: 9,
    color: "#666666",
    marginTop: 1,
  },
  // Skills
  skillsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  skillItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    width: "33.33%",
    marginBottom: 4,
    paddingRight: 8,
  },
  skillBullet: {
    fontSize: 10,
    color: "#000000",
    marginRight: 4,
  },
  skillText: {
    fontSize: 10,
    color: "#000000",
    flex: 1,
  },
  // Projects
  projectItem: {
    marginBottom: 10,
  },
  projectTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#000000",
  },
  projectTech: {
    fontSize: 9,
    color: "#666666",
    fontStyle: "italic",
    marginTop: 1,
  },
  // Languages
  languageItem: {
    flexDirection: "row",
    marginBottom: 4,
  },
  languageName: {
    fontSize: 10,
    color: "#000000",
    marginRight: 8,
  },
  languageLevel: {
    fontSize: 10,
    color: "#666666",
  },
  // Summary
  summaryText: {
    fontSize: 10,
    lineHeight: 1.6,
    color: "#000000",
    textAlign: "justify",
  },
});

const CleanProfessionalTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
    customSections = [],
    sectionTitles = {},
    summary = "",
    professionalTitle = "",
  } = resumeData || {};

  // Create dynamic styles
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { fontSize, sectionSpacing, titleColor, fontFamily } = customizations;
    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    if (fontSize) {
      customStyleOverrides.name = { ...baseStyles.name, fontSize: fontSize * 2 };
      customStyleOverrides.sectionLabel = { ...baseStyles.sectionLabel, fontSize: fontSize * 0.72 };
      customStyleOverrides.summaryText = { ...baseStyles.summaryText, fontSize: fontSize * 0.72 };
      customStyleOverrides.bulletPoint = { ...baseStyles.bulletPoint, fontSize: fontSize * 0.72 };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionRow = { ...baseStyles.sectionRow, marginBottom: sectionSpacing };
    }

    if (titleColor) {
      customStyleOverrides.name = {
        ...(customStyleOverrides.name || baseStyles.name),
        color: titleColor,
      };
      customStyleOverrides.sectionLabel = {
        ...(customStyleOverrides.sectionLabel || baseStyles.sectionLabel),
        color: titleColor,
      };
    }

    return StyleSheet.create({ ...baseStyles, ...customStyleOverrides });
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Helper to get skill name
  const getSkillName = (skill) => {
    if (typeof skill === "string") return skill;
    if (skill && typeof skill === "object") return skill.name || skill.skill || "";
    return "";
  };

  // Combine all skills and remove duplicates
  const allSkills = (() => {
    let skillsList = [];

    if (Array.isArray(skills)) {
      skillsList = skills;
    } else if (skills.list && Array.isArray(skills.list) && skills.list.length > 0) {
      skillsList = skills.list;
    } else {
      const combinedSkills = [
        ...(skills.technical || []),
        ...(skills.soft || []),
        ...(skills.tools || []),
        ...(skills.other || []),
      ];
      if (combinedSkills.length > 0) {
        skillsList = combinedSkills;
      } else if (skills.categorized) {
        skillsList = [
          ...(skills.categorized.technical || []),
          ...(skills.categorized.soft || []),
          ...(skills.categorized.tools || []),
          ...(skills.categorized.other || []),
        ];
      }
    }

    // Remove duplicates by skill name
    const seen = new Set();
    return skillsList.filter((skill) => {
      const name = getSkillName(skill).toLowerCase().trim();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  })();

  // Section titles with custom title support
  const defaultTitles = {
    summary: 'Summary',
    workExperience: 'Professional\nExperience',
    education: 'Education',
    skills: 'Skills',
    projects: 'Projects',
    certifications: 'Certificates',
    achievements: 'Achievements',
    languages: 'Languages',
    awards: 'Awards',
    volunteering: 'Volunteering',
    interests: 'Interests',
    industryExpertise: 'Industry\nExpertise',
    publications: 'Publications',
    references: 'References',
  };
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key] || key;

  // Define section render functions
  const renderSummary = () =>
    summary && isValidSummary(summary) ? (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>Summary</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          <Text style={mergedStyles.summaryText}>{parseHtmlToText(summary)}</Text>
        </View>
      </View>
    ) : null;

  const renderWorkExperience = () => {
    if (!workExperience || workExperience.length === 0) return null;

    // Remove duplicate work experiences by company + jobTitle
    const seen = new Set();
    const uniqueWorkExperience = workExperience.filter((job) => {
      const key = `${(job.company || "").toLowerCase().trim()}-${(job.jobTitle || "").toLowerCase().trim()}`;
      if (!key || key === "-" || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (uniqueWorkExperience.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>Professional{"\n"}Experience</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          {uniqueWorkExperience.map((job, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <View style={mergedStyles.experienceHeader}>
                <Text style={mergedStyles.jobTitleDate}>
                  {job.jobTitle} | {formatDate(job.startDate)}-{job.isCurrentJob || job.endDate === "Present" || !job.endDate ? "Present" : formatDate(job.endDate)}
                </Text>
              </View>
              <Text style={mergedStyles.companyName}>{job.company}</Text>
              {job.description && renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderEducation = () => {
    if (!education || education.length === 0) return null;

    // Remove duplicate education by school + degree
    const seen = new Set();
    const uniqueEducation = education.filter((edu) => {
      const key = `${(edu.school || "").toLowerCase().trim()}-${(edu.degree || "").toLowerCase().trim()}`;
      if (!key || key === "-" || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (uniqueEducation.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>Education</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          {uniqueEducation.map((edu, index) => (
            <View key={index} style={mergedStyles.educationItem}>
              <View style={mergedStyles.schoolDateRow}>
                <Text style={mergedStyles.schoolName}>
                  {edu.school} | {formatDate(edu.startDate)}-{edu.endDate === "Present" ? "Present" : formatDate(edu.endDate)}
                </Text>
              </View>
              <Text style={mergedStyles.degree}>
                {edu.degree}
                {edu.fieldOfStudy ? ` in ${edu.fieldOfStudy}` : ""}
              </Text>
              {edu.description && renderBulletPoints(parseHtmlToText(edu.description), mergedStyles)}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderSkills = () =>
    allSkills.length > 0 ? (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>Skills</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          <View style={mergedStyles.skillsContainer}>
            {allSkills.map((skill, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillBullet}>•</Text>
                <Text style={mergedStyles.skillText}>{getSkillName(skill)}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    ) : null;

  const renderProjects = () => {
    if (!projects || projects.length === 0) return null;

    // Remove duplicate projects by name
    const seen = new Set();
    const uniqueProjects = projects.filter((project) => {
      const name = (project.name || "").toLowerCase().trim();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });

    if (uniqueProjects.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>Projects</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          {uniqueProjects.map((project, index) => (
            <View key={index} style={mergedStyles.projectItem}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {project.technologies && <Text style={mergedStyles.projectTech}>Technologies: {project.technologies}</Text>}
              {project.description && (
                <View style={{ marginTop: 3 }}>{renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}</View>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderCertifications = () => {
    if (!certifications || certifications.length === 0) return null;

    // Remove duplicate certifications by name
    const seen = new Set();
    const uniqueCertifications = certifications.filter((cert) => {
      const name = (cert.name || "").toLowerCase().trim();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });

    if (uniqueCertifications.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>Certificates</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          <View style={mergedStyles.certificationsGrid}>
            {uniqueCertifications.map((cert, index) => (
              <View key={index} style={mergedStyles.certificationItem}>
                <Text style={mergedStyles.certName}>
                  {cert.name} | {formatDate(cert.date)}
                </Text>
                <Text style={mergedStyles.certAuthority}>{cert.authority || cert.issuer}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  };

  const renderAchievements = () => {
    if (!achievements || achievements.length === 0) return null;

    // Remove duplicate achievements by description
    const seen = new Set();
    const uniqueAchievements = achievements.filter((achievement) => {
      const desc = (achievement.description || "").toLowerCase().trim();
      if (!desc || seen.has(desc)) return false;
      seen.add(desc);
      return true;
    });

    if (uniqueAchievements.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>Achievements</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          {uniqueAchievements.map((achievement, index) => (
            <View key={index} style={{ marginBottom: 5 }}>
              {achievement.description && (
                <Text style={mergedStyles.summaryText}>{parseHtmlToText(achievement.description)}</Text>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  };

  // Helper to get language name from string or object
  const getLanguageName = (lang) => {
    if (typeof lang === "string") return lang;
    if (lang && typeof lang === "object") return lang.name || lang.language || "";
    return "";
  };

  // Helper to get interest/expertise name from string or object
  const getItemName = (item) => {
    if (typeof item === "string") return item;
    if (item && typeof item === "object") return item.name || item.title || "";
    return "";
  };

  const renderLanguages = () => {
    if (!languages || languages.length === 0) return null;

    // Remove duplicate languages
    const seen = new Set();
    const uniqueLanguages = languages.filter((lang) => {
      const name = getLanguageName(lang).toLowerCase().trim();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });

    if (uniqueLanguages.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>{getSectionTitle('languages')}</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          {uniqueLanguages.map((lang, index) => (
            <View key={index} style={mergedStyles.languageItem}>
              <Text style={mergedStyles.languageName}>{getLanguageName(lang)}</Text>
              {lang.proficiency && <Text style={mergedStyles.languageLevel}>- {lang.proficiency}</Text>}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderAwards = () => {
    if (!awards || awards.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>{getSectionTitle('awards')}</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          {awards.map((award, index) => (
            <View key={index} style={{ marginBottom: 8 }}>
              <Text style={mergedStyles.projectTitle}>{award.title || award.name}</Text>
              {award.issuer && <Text style={mergedStyles.projectTech}>{award.issuer}</Text>}
              {award.date && <Text style={mergedStyles.projectTech}>{formatDate(award.date)}</Text>}
              {award.description && <Text style={mergedStyles.summaryText}>{parseHtmlToText(award.description)}</Text>}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderVolunteering = () => {
    if (!volunteering || volunteering.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>{getSectionTitle('volunteering')}</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          {volunteering.map((vol, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <View style={mergedStyles.experienceHeader}>
                <Text style={mergedStyles.jobTitleDate}>
                  {vol.role || vol.position} | {formatDate(vol.startDate)}-{vol.endDate === "Present" ? "Present" : formatDate(vol.endDate)}
                </Text>
              </View>
              <Text style={mergedStyles.companyName}>{vol.organization}</Text>
              {vol.description && renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderInterests = () => {
    if (!interests || interests.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>{getSectionTitle('interests')}</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          <View style={mergedStyles.skillsContainer}>
            {interests.map((interest, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillBullet}>•</Text>
                <Text style={mergedStyles.skillText}>
                  {getItemName(interest)}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  };

  const renderIndustryExpertise = () => {
    if (!industryExpertise || industryExpertise.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>{getSectionTitle('industryExpertise')}</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          <View style={mergedStyles.skillsContainer}>
            {industryExpertise.map((expertise, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillBullet}>•</Text>
                <Text style={mergedStyles.skillText}>
                  {getItemName(expertise)}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  };

  const renderPublications = () => {
    if (!publications || publications.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>{getSectionTitle('publications')}</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          {publications.map((pub, index) => (
            <View key={index} style={{ marginBottom: 8 }}>
              <Text style={mergedStyles.projectTitle}>{pub.title}</Text>
              {pub.publisher && <Text style={mergedStyles.projectTech}>{pub.publisher}</Text>}
              {pub.date && <Text style={mergedStyles.projectTech}>{formatDate(pub.date)}</Text>}
              {pub.description && <Text style={mergedStyles.summaryText}>{parseHtmlToText(pub.description)}</Text>}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderReferences = () => {
    if (!references || references.length === 0) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>{getSectionTitle('references')}</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          <View style={mergedStyles.certificationsGrid}>
            {references.map((ref, index) => (
              <View key={index} style={mergedStyles.certificationItem}>
                <Text style={mergedStyles.certName}>{ref.name}</Text>
                {ref.title && <Text style={mergedStyles.certAuthority}>{ref.title}</Text>}
                {ref.company && <Text style={mergedStyles.certAuthority}>{ref.company}</Text>}
                {ref.phone && <Text style={mergedStyles.certAuthority}>{ref.phone}</Text>}
                {ref.email && <Text style={mergedStyles.certAuthority}>{ref.email}</Text>}
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  };

  // Render custom sections dynamically
  const renderCustomSection = (customSection) => {
    if (!customSection.id || !customSection.title || !customSection.items?.length) return null;

    return (
      <View style={mergedStyles.sectionRow}>
        <View style={mergedStyles.sectionLabelColumn}>
          <Text style={mergedStyles.sectionLabel}>{customSection.title}</Text>
        </View>
        <View style={mergedStyles.sectionContentColumn}>
          {customSection.items.map((item, index) => (
            <View key={index} style={{ marginBottom: 5 }}>
              {item.content && <Text style={mergedStyles.summaryText}>{parseHtmlToText(item.content)}</Text>}
            </View>
          ))}
        </View>
      </View>
    );
  };

  // Section mapping for custom ordering
  const sectionRenderers = {
    summary: renderSummary,
    workExperience: renderWorkExperience,
    education: renderEducation,
    skills: renderSkills,
    projects: renderProjects,
    certifications: renderCertifications,
    achievements: renderAchievements,
    languages: renderLanguages,
    awards: renderAwards,
    volunteering: renderVolunteering,
    interests: renderInterests,
    industryExpertise: renderIndustryExpertise,
    publications: renderPublications,
    references: renderReferences,
    // Add custom sections dynamically
    ...customSections.reduce((acc, cs) => {
      if (cs.id && cs.title && cs.items?.length > 0) {
        acc[cs.id] = () => renderCustomSection(cs);
      }
      return acc;
    }, {}),
  };

  // Default section order - includes all possible sections
  const defaultSectionOrder = [
    "summary",
    "workExperience",
    "education",
    "skills",
    "projects",
    "certifications",
    "achievements",
    "languages",
    "awards",
    "volunteering",
    "interests",
    "industryExpertise",
    "publications",
    "references",
  ];

  // Include custom sections in the final order
  const customSectionIds = customSections
    .filter(cs => cs.id && cs.title && cs.items?.length > 0)
    .map(cs => cs.id);

  // Get the section order to use - remove duplicates and include custom sections
  const sectionOrder = (() => {
    const baseOrder = customSectionOrder && customSectionOrder.length > 0 ? customSectionOrder : defaultSectionOrder;
    const fullOrder = [
      ...baseOrder,
      ...customSectionIds.filter(id => !baseOrder.includes(id))
    ];
    const seen = new Set();
    return fullOrder.filter((sectionId) => {
      if (seen.has(sectionId)) return false;
      seen.add(sectionId);
      return true;
    });
  })();

  // Render sections in order using the same pattern as other templates
  const renderOrderedSections = () => {
    return sectionOrder.map((sectionId, index) => {
      const renderer = sectionRenderers[sectionId];
      const content = renderer ? renderer() : null;
      return content ? <View key={`${sectionId}-${index}`} wrap={true}>{content}</View> : null;
    });
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header - Name on left, Title on right */}
        <View style={mergedStyles.headerContainer}>
          <Text style={mergedStyles.name}>
            {personalInfo.firstName} {personalInfo.lastName}
          </Text>
          {(professionalTitle || (workExperience.length > 0 && workExperience[0].jobTitle)) && (
            <Text style={mergedStyles.title}>{professionalTitle || workExperience[0].jobTitle}</Text>
          )}
        </View>

        {/* Contact Section - Always first */}
        <View style={mergedStyles.sectionRow}>
          <View style={mergedStyles.sectionLabelColumn}>
            <Text style={mergedStyles.sectionLabel}>Contact</Text>
          </View>
          <View style={mergedStyles.sectionContentColumn}>
            <View style={mergedStyles.contactGrid}>
              <View style={mergedStyles.contactColumn}>
                {personalInfo.phone && (
                  <View style={mergedStyles.contactItem}>
                    <Text style={mergedStyles.contactLabel}>Phone:</Text>
                    <Text style={mergedStyles.contactValue}>{formatPhoneNumber(personalInfo.phone)}</Text>
                  </View>
                )}
                {personalInfo.email && (
                  <View style={mergedStyles.contactItem}>
                    <Text style={mergedStyles.contactLabel}>Email:</Text>
                    <Text style={mergedStyles.contactValue}>{personalInfo.email}</Text>
                  </View>
                )}
              </View>
              <View style={mergedStyles.contactColumn}>
                {personalInfo.location && (
                  <View style={mergedStyles.contactItem}>
                    <Text style={mergedStyles.contactLabel}>Address:</Text>
                    <Text style={mergedStyles.contactValue}>{personalInfo.location}</Text>
                  </View>
                )}
                {(personalInfo.linkedin || personalInfo.website) && (
                  <View style={mergedStyles.contactItem}>
                    <Text style={mergedStyles.contactLabel}>Portfolio:</Text>
                    <Text style={mergedStyles.contactValue}>{personalInfo.linkedin || personalInfo.website}</Text>
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Render sections in custom order */}
        {renderOrderedSections()}
      </Page>
    </Document>
  );
};

export default CleanProfessionalTemplate;
